import { useCallback, useEffect, useRef, useState } from "react";
import { getMyQuota } from "../../api/client";

interface TokenUsageDisplayProps {
  className?: string;
  /** ``"pill"`` — compact standalone counter. ``"menu"`` — a full-width
   *  profile-menu row ("Token usage · 953.4K"); ``className`` styles the row
   *  button so it matches its sibling menu items. */
  variant?: "pill" | "menu";
  // Live tokens accumulated during active run (takes precedence over quota API)
  liveTokens?: {
    input: number;
    output: number;
    thinking: number;
  } | null;
}

// Count sits at the right end of the menu row, in the counter's blue.
const menuCountStyle = {
  marginLeft: "auto",
  paddingLeft: "12px",
  fontFamily: "monospace",
  fontWeight: 600,
  color: "#2E66D2",
} as const;

function TokenIcon(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" style={{ opacity: 0.9, flexShrink: 0 }}>
      <title>Token usage</title>
      <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 1a6 6 0 110 12A6 6 0 018 2zm0 2a4 4 0 100 8 4 4 0 000-8zm0 1a3 3 0 110 6 3 3 0 010-6z" />
    </svg>
  );
}

/** The menu row already reads "Token usage", so its icon stays decorative. */
function MenuTokenIcon(): JSX.Element {
  return (
    <span aria-hidden="true" style={{ display: "inline-flex" }}>
      <TokenIcon />
    </span>
  );
}

/**
 * Compact token usage display with VS Code-style popover.
 * Shows an icon with total tokens. On hover, displays detailed breakdown.
 */
export default function TokenUsageDisplay({
  className = "",
  variant = "pill",
  liveTokens = null,
}: TokenUsageDisplayProps): JSX.Element | null {
  const [quota, setQuota] = useState<{
    input_tokens_used: number;
    output_tokens_used: number;
    input_tokens_limit: number | null;
    output_tokens_limit: number | null;
  } | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [showPopover, setShowPopover] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showPopover) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node) &&
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setShowPopover(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPopover]);

  // Tracks whether the component is still mounted so async fetches don't
  // set state after unmount. A ref (not effect-local) lets the shared
  // fetchQuota below be reused by both the mount effect and the
  // run-completion effect.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchQuota = useCallback(async () => {
    try {
      const data = await getMyQuota();
      if (mountedRef.current) {
        setQuota({
          input_tokens_used: data.input_tokens_used,
          output_tokens_used: data.output_tokens_used,
          input_tokens_limit: data.input_tokens_limit,
          output_tokens_limit: data.output_tokens_limit,
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Failed to fetch quota:", error);
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  // Fetch once on mount. No interval polling — token usage only changes
  // when a run consumes tokens, so we refresh event-driven (below) instead
  // of hammering /api/subscriptions/me on a timer.
  useEffect(() => {
    fetchQuota();
  }, [fetchQuota]);

  // Refresh the persisted baseline when a run finishes. The parent passes
  // a non-null `liveTokens` only while a run is active, so a non-null → null
  // transition marks run completion — exactly when the server-side usage
  // total has just changed.
  const prevLiveRef = useRef(liveTokens);
  useEffect(() => {
    const wasActive = prevLiveRef.current !== null;
    const nowIdle = liveTokens === null;
    if (wasActive && nowIdle) {
      fetchQuota();
    }
    prevLiveRef.current = liveTokens;
  }, [liveTokens, fetchQuota]);

  const formatTokens = (count: number): string => {
    if (count >= 1_000_000) {
      return `${(count / 1_000_000).toFixed(1)}M`;
    }
    if (count >= 1_000) {
      return `${(count / 1_000).toFixed(1)}K`;
    }
    return count.toString();
  };

  // The menu row mounts when the profile menu opens, so show it (with a
  // placeholder count) while the quota loads rather than popping in late.
  if (variant === "menu" && isLoading) {
    return (
      <button type="button" className={className} role="menuitem" disabled>
        <MenuTokenIcon />
        <span>Token usage</span>
        <span style={menuCountStyle}>…</span>
      </button>
    );
  }

  if (isLoading || !quota) {
    return null;
  }

  const displayInput = liveTokens ? quota.input_tokens_used + liveTokens.input : quota.input_tokens_used;
  const displayOutput = liveTokens ? quota.output_tokens_used + liveTokens.output : quota.output_tokens_used;

  const totalUsed = displayInput + displayOutput;
  const totalLimit = (quota.input_tokens_limit || 0) + (quota.output_tokens_limit || 0);
  const usagePercent = totalLimit > 0 ? Math.round((totalUsed / totalLimit) * 100) : 0;

  const inputPercent = quota.input_tokens_limit ? Math.round((displayInput / quota.input_tokens_limit) * 100) : 0;
  const outputPercent = quota.output_tokens_limit ? Math.round((displayOutput / quota.output_tokens_limit) * 100) : 0;

  const handleToggle = () => {
    if (!showPopover && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const popoverWidth = 280;
      const margin = 8;
      const idealLeft = rect.left + rect.width / 2;
      const clampedLeft = Math.min(
        Math.max(idealLeft, popoverWidth / 2 + margin),
        window.innerWidth - popoverWidth / 2 - margin,
      );
      setPopoverPos({
        top: rect.bottom + 8,
        left: clampedLeft,
      });
    }
    setShowPopover((v) => !v);
  };

  return (
    <div
      ref={triggerRef}
      className={variant === "menu" ? "token-usage-display" : `token-usage-display ${className}`}
      style={{ display: variant === "menu" ? "block" : "inline-block" }}
    >
      {variant === "menu" ? (
        <button
          type="button"
          className={className}
          role="menuitem"
          aria-expanded={showPopover}
          onClick={handleToggle}
          style={{ width: "100%" }}
        >
          <MenuTokenIcon />
          <span>Token usage</span>
          <span style={menuCountStyle}>{formatTokens(totalUsed)}</span>
        </button>
      ) : (
        <button
          type="button"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            padding: "3px 8px",
            fontSize: "11px",
            fontFamily: "monospace",
            fontWeight: 600,
            color: "#2E66D2",
            cursor: "pointer",
            borderRadius: "999px",
            backgroundColor: showPopover ? "#E8F1FF" : "#F3F8FF",
            border: "1px solid #D8E6FF",
            outline: "none",
            lineHeight: 1,
          }}
          onClick={handleToggle}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") handleToggle();
          }}
        >
          <TokenIcon />
          <span>{formatTokens(totalUsed)}</span>
        </button>
      )}

      {/* Popover — fixed so it never affects document layout */}
      {showPopover && (
        <div
          ref={popoverRef}
          style={{
            position: "fixed",
            top: popoverPos.top,
            left: popoverPos.left,
            transform: "translateX(-50%)",
            zIndex: 9999,
            padding: "12px",
            backgroundColor: "#252526",
            border: "1px solid #3e3e42",
            borderRadius: "4px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
            minWidth: "280px",
            fontSize: "12px",
            color: "#cccccc",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          }}
        >
          {/* Header */}
          <div
            style={{
              marginBottom: "12px",
              paddingBottom: "8px",
              borderBottom: "1px solid #3e3e42",
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: "4px" }}>Token Usage</div>
            {totalLimit > 0 && (
              <div style={{ fontSize: "11px", color: "#858585" }}>
                {usagePercent}% used ({formatTokens(totalUsed)} / {formatTokens(totalLimit)})
              </div>
            )}
          </div>

          {/* Input Tokens */}
          <div style={{ marginBottom: "12px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "4px",
                fontSize: "11px",
              }}
            >
              <span>Input Tokens</span>
              <span style={{ fontFamily: "monospace", color: "#ffffff" }}>
                {displayInput.toLocaleString()}
                {quota.input_tokens_limit && ` / ${quota.input_tokens_limit.toLocaleString()}`}
              </span>
            </div>
            {quota.input_tokens_limit && (
              <div
                style={{
                  height: "4px",
                  backgroundColor: "#3e3e42",
                  borderRadius: "2px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${Math.min(inputPercent, 100)}%`,
                    backgroundColor: inputPercent > 80 ? "#f48771" : "#007ACC",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            )}
            {liveTokens && liveTokens.input > 0 && (
              <div
                style={{
                  fontSize: "10px",
                  color: "#007ACC",
                  marginTop: "2px",
                }}
              >
                +{liveTokens.input.toLocaleString()} this run
              </div>
            )}
          </div>

          {/* Output Tokens */}
          <div style={{ marginBottom: "8px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "4px",
                fontSize: "11px",
              }}
            >
              <span>Output Tokens</span>
              <span style={{ fontFamily: "monospace", color: "#ffffff" }}>
                {displayOutput.toLocaleString()}
                {quota.output_tokens_limit && ` / ${quota.output_tokens_limit.toLocaleString()}`}
              </span>
            </div>
            {quota.output_tokens_limit && (
              <div
                style={{
                  height: "4px",
                  backgroundColor: "#3e3e42",
                  borderRadius: "2px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${Math.min(outputPercent, 100)}%`,
                    backgroundColor: outputPercent > 80 ? "#f48771" : "#007ACC",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            )}
            {liveTokens && liveTokens.output > 0 && (
              <div
                style={{
                  fontSize: "10px",
                  color: "#007ACC",
                  marginTop: "2px",
                }}
              >
                +{liveTokens.output.toLocaleString()} this run
              </div>
            )}
          </div>

          {/* Live indicator */}
          {liveTokens && (
            <div
              style={{
                marginTop: "8px",
                paddingTop: "8px",
                borderTop: "1px solid #3e3e42",
                fontSize: "10px",
                color: "#007ACC",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <div
                style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  backgroundColor: "#007ACC",
                  animation: "pulse 2s ease-in-out infinite",
                }}
              />
              <span>Live tracking active</span>
            </div>
          )}
        </div>
      )}

      {/* Pulse animation */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>
    </div>
  );
}
